<script lang="ts" setup>
import { ref, watch } from 'vue';
import _random from 'lodash/random';

import { useLoaderStore } from '@/stores/useLoaderStore';

const loaderStore = useLoaderStore();

const startingPoint = ref(0);
const endingPoint = ref(97);
const progress = ref(startingPoint.value);
const timeoutId = ref<ReturnType<typeof setTimeout> | undefined>(undefined);

const defaultDuration = 10000;
const defaultInterval = 800;
const variation = 0.5;

const loop = (): void => {
  if (timeoutId.value) {
    clearTimeout(timeoutId.value);
  }
  if (progress.value >= endingPoint.value) {
    return;
  }
  const size =
    (endingPoint.value - startingPoint.value) /
    (defaultDuration / defaultInterval);
  const p = Math.round(
    progress.value +
      // TODO
      /* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion */
      Number(_random(size * (1 - variation), size * (1 + variation))),
  );
  progress.value = Math.min(p, endingPoint.value);
  timeoutId.value = setTimeout(
    loop,
    _random(
      defaultInterval * (1 - variation),
      defaultInterval * (1 + variation),
    ),
  );
};

const start = (): void => {
  progress.value = startingPoint.value;
  loop();
};

const stop = (): void => {
  progress.value = 100;
  clearTimeout(timeoutId.value);
};

watch(
  () => loaderStore.isGlobalLoading,
  (data) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    data ? start() : stop();
  },
  { immediate: true },
);
</script>

<template>
  <div :class="$style.globalLoader" data-testid="globalLoader">
    <v-progress-linear
      v-if="loaderStore.isGlobalLoading"
      v-model="progress"
      :color="$style.loaderColor"
      :class="$style.mainLoader"
    />
    <v-progress-linear
      v-if="loaderStore.isGlobalLoading && progress >= endingPoint"
      v-model="progress"
      indeterminate
      :color="$style.loaderColor"
      :style="`width: ${progress}%;`"
      :class="$style.backLoader"
    />
    <v-progress-circular
      v-if="loaderStore.isGlobalLoading"
      indeterminate
      size="20"
      width="3"
      :color="$style.loaderColor"
      :class="$style.spinner"
    />
  </div>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';

:export {
  loaderColor: colors.$color-brand-400;
}

.globalLoader {
  position: relative;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  .mainLoader {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    z-index: 10000;
    opacity: 0.7;
  }
  .backLoader {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 9999;
    margin: 0;
    padding: 0;
    left: 0 !important;
    transform: none !important;
    opacity: 0.9;
  }
  .spinner {
    position: absolute;
    top: 12px;
    right: 8px;
    z-index: 10000;
    opacity: 0.8;
  }
}
</style>
